import type {
  BuildingAnalyticBRep,
  BuildingAnalyticEntity,
  BuildingAnalyticEntityHandle,
  BuildingAnalyticExtrusion,
  BuildingAnalyticLineSegment,
  BuildingAnalyticPolyLine,
  BuildingAnalyticFace,
} from "building-types/buildingAnalyticEntityTypes";
import {
  isBuildingGeomAnalyticTag,
  isBuildingGeomAttributeTagOfNameAndValueType,
  isBuildingGeomOperationTag,
  isBuildingGeomParentTag,
  isBuildingGeomPositionTag,
} from "building-types/buildingGeomTag/buildingGeomTags";
import type {
  BuildingGeomAttributeTag,
  BuildingGeomAttributeValueType,
  BuildingGeomOperation,
  BuildingGeomParent,
  BuildingGeomParentType,
  BuildingGeomPosition,
  BuildingGeomTag,
  FacePosition,
  OperationType,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import {
  BALUSTER,
  COLUMN,
  DOOR_TRIM,
  EDGE,
  EXTERIOR_FINISH_TAG,
  FLASHING,
  FLOOR_STRUCTURE,
  INFILL,
  INTERIOR_FINISH_TAG,
  POST,
  RAIL,
  RAILING,
  SOLID,
  WALL_DOMAIN_OPENING_INTERSECTION,
  WINDOW_TRIM,
  type BuildingGeomAnalytic,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { BuildingEntity } from "building-types/entities/buildingEntity";
import type { BuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import { isBuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import { isBuildingAnalyticEntityType } from "building-types/entities/buildingEntityTypeTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Table } from "common/data/table";
import {
  docEntityHandleIsGeneratedEntity,
  isDocEntityHandle,
  docEntityHandleEquals,
} from "doc/entity/docEntityHandleTools";

export function isBuildingAnalyticEntity(entity: BuildingEntity): entity is BuildingAnalyticEntity {
  return isBuildingAnalyticEntityType(entity.entityType);
}

export function isBuildingAnalyticBRep(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticBRep {
  return entity?.geometryType === "BREP";
}

export function isBuildingAnalyticExtrusion(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticExtrusion {
  return !!entity && entity.geometryType === "EXTRUSION";
}

export function isBuildingAnalyticLineSegment(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticLineSegment {
  return entity?.geometryType === "LINE_SEGMENT";
}

export function isBuildingAnalyticPolyLine(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticPolyLine {
  return entity?.geometryType === "POLYLINE";
}

export function isBuildingAnalyticFace(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticFace {
  return entity?.geometryType === "FACE";
}

export function isBuildingAnalyticEntityHandle(handle: BuildingEntityHandle): handle is BuildingAnalyticEntityHandle {
  return isBuildingAnalyticEntityType(handle.entityType);
}

export function buildingGetAnalyticEntities<T extends BuildingAnalyticEntity>(
  analyticEntities: Table<BuildingAnalyticEntity>,
  predicate?: (entity: BuildingAnalyticEntity) => entity is T,
): T[] {
  const entities: T[] = [];
  for (const entity of Object.values(analyticEntities)) {
    if (predicate?.(entity)) {
      entities.push(entity);
    }
  }
  return entities;
}

export function childAnalyticEntities(
  analyticEntities: Table<BuildingAnalyticEntity>,
  handle: DocEntityHandle | BuildingEntityHandle,
) {
  return buildingGetAnalyticEntities(analyticEntities, isChildOfEntity(handle));
}

export function parentBuildingHandles(
  entity: BuildingAnalyticEntity,
  predicate?: (handle: BuildingEntityHandle) => boolean,
): BuildingEntityHandle[] {
  const handles: BuildingEntityHandle[] = [];
  for (const parent of entity.parents) {
    if (!docEntityHandleIsGeneratedEntity(parent)) {
      continue;
    }
    const handle = parent.entity;
    if (isBuildingEntityHandle(handle) && (predicate?.(handle) ?? true)) {
      handles.push(handle);
    }
  }
  return handles;
}

export function geomAnalyticTags(entity: BuildingAnalyticEntity): BuildingGeomAnalytic[] {
  return entity.tags.filter(isBuildingGeomAnalyticTag);
}

export function geomPositionTags(entity: BuildingAnalyticEntity): BuildingGeomPosition[] {
  return entity.tags.filter(isBuildingGeomPositionTag);
}

export function geomParentTags(entity: BuildingAnalyticEntity): BuildingGeomParent[] {
  return entity.tags.filter(isBuildingGeomParentTag);
}

export function geomOperationTags(entity: BuildingAnalyticEntity): BuildingGeomOperation[] {
  return entity.tags.filter(isBuildingGeomOperationTag);
}

export function geomAttributeTag<T extends BuildingGeomAttributeValueType>(
  entity: BuildingAnalyticEntity,
  name: string,
  valueType: T,
): Extract<BuildingGeomAttributeTag<T>, { valueType: T }> | undefined {
  return entity.tags.find(isBuildingGeomAttributeTagOfNameAndValueType(name, valueType));
}

export function isCeilingFace(entity: BuildingAnalyticEntity): entity is BuildingAnalyticFace {
  return isBuildingAnalyticFace(entity) && parentBuildingHandles(entity).some((p) => p.entityType === "CEILING");
}

export function isRoofFace(entity: BuildingAnalyticEntity): entity is BuildingAnalyticFace {
  return (
    isBuildingAnalyticFace(entity) &&
    parentBuildingHandles(entity).some((p) => p.entityType === "ROOF_SYSTEM") &&
    !isRoofSoffitEntity(entity) // explicit exclude soffit faces
  );
}

export function isRoofEdge(entity: BuildingAnalyticEntity): entity is BuildingAnalyticLineSegment {
  return (
    isBuildingAnalyticLineSegment(entity) &&
    parentBuildingHandles(entity).some((p) => p.entityType === "ROOF_SYSTEM") &&
    !isRoofSoffitEntity(entity) && // explicit exclude soffit faces
    !isFlashingEntity(entity) // explicit exclude flashing line segments
  );
}

export function isFoundationVolume(entity: BuildingAnalyticEntity): boolean {
  return (
    isBuildingAnalyticExtrusion(entity) &&
    parentBuildingHandles(entity).some(
      (p) => p.entityType === "FOUNDATION_SYSTEM" || p.entityType === "FOUNDATION_SYSTEM_HORIZONTAL_ASSEMBLY",
    )
  );
}

export function isRailingEntity(entity: BuildingAnalyticEntity): entity is BuildingAnalyticEntity {
  return isBuildingAnalyticEntity(entity) && entity.tags.includes(RAILING);
}

export function isColumnEntity(entity: BuildingAnalyticEntity): entity is BuildingAnalyticExtrusion {
  return isBuildingAnalyticExtrusion(entity) && entity.tags.includes(COLUMN);
}

export function isRailingPost(entity: BuildingAnalyticEntity): entity is BuildingAnalyticExtrusion {
  return isBuildingAnalyticExtrusion(entity) && entity.tags.includes(RAILING) && entity.tags.includes(POST);
}

export function isRailingInfill(
  entity: BuildingAnalyticEntity,
): entity is BuildingAnalyticBRep | BuildingAnalyticExtrusion {
  return (
    (isBuildingAnalyticBRep(entity) || isBuildingAnalyticExtrusion(entity)) &&
    entity.tags.includes(RAILING) &&
    entity.tags.includes(INFILL)
  );
}

export function isRailingInfillBaluster(entity: BuildingAnalyticEntity): entity is BuildingAnalyticExtrusion {
  return isBuildingAnalyticExtrusion(entity) && isRailingInfill(entity) && entity.tags.includes(BALUSTER);
}

export function isRailingInfillSolid(
  entity: BuildingAnalyticEntity,
): entity is BuildingAnalyticBRep | BuildingAnalyticExtrusion {
  return isRailingInfill(entity) && entity.tags.includes(SOLID);
}

export function isRailingRail(entity: BuildingAnalyticEntity): entity is BuildingAnalyticBRep {
  return isBuildingAnalyticBRep(entity) && entity.tags.includes(RAILING) && entity.tags.includes(RAIL);
}

export function isFoundationEdge(entity: BuildingAnalyticEntity): boolean {
  return (
    isBuildingAnalyticLineSegment(entity) &&
    parentBuildingHandles(entity).some(
      (p) => p.entityType === "FOUNDATION_SYSTEM" || p.entityType === "FOUNDATION_SYSTEM_HORIZONTAL_ASSEMBLY",
    )
  );
}

export function isChildOfEntity(
  handle: BuildingEntityHandle | DocEntityHandle,
): (e: BuildingAnalyticEntity) => e is BuildingAnalyticEntity {
  if (isBuildingEntityHandle(handle)) {
    return (entity: BuildingAnalyticEntity): entity is BuildingAnalyticEntity =>
      entity.parents && parentBuildingHandles(entity).some((p) => p.id === handle.id);
  }
  if (isDocEntityHandle(handle)) {
    return (entity: BuildingAnalyticEntity): entity is BuildingAnalyticEntity =>
      entity.parents && entity.parents.filter(isDocEntityHandle).some((p) => docEntityHandleEquals(p, handle));
  }
  throw new Error("Invalid handle type");
}

export function isFoundationSlab(entity: BuildingAnalyticEntity): entity is BuildingAnalyticExtrusion {
  return isBuildingAnalyticExtrusion(entity) && parentBuildingHandles(entity).some((p) => p.entityType === "SLAB");
}

export function isFoundationFooting(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticEntity {
  return !!entity && geomAnalyticTags(entity).some((t) => t.condition === "FOOTING");
}

export function isFoundationStemWall(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticEntity {
  return !!entity && geomAnalyticTags(entity).some((t) => t.condition === "STEMWALL");
}

export function isFoundationSection(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticEntity {
  return !!entity && parentBuildingHandles(entity).some((p) => p.entityType === "FOUNDATION_SECTION");
}

export function isRoofSoffitEntity(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticEntity {
  return !!entity && parentBuildingHandles(entity).some((p) => p.entityType === "ROOF_SOFFIT");
}

export function isFlashingEntity(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticEntity {
  return !!entity && entity.tags.includes(FLASHING);
}

export function isRoofSoffitDepth(entity: BuildingAnalyticEntity): entity is BuildingAnalyticLineSegment {
  return isRoofSoffitEntity(entity) && geomAnalyticTags(entity).some((t) => t.condition === "SOFFIT_DEPTH");
}

export function isRoofSoffitRail(entity: BuildingAnalyticEntity): entity is BuildingAnalyticLineSegment {
  return isRoofSoffitEntity(entity) && geomAnalyticTags(entity).some((t) => t.condition === "SOFFIT_RAIL");
}

export function isDoorTrim(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticEntity {
  return !!entity && entity.tags.includes(DOOR_TRIM);
}

export function isWindowTrim(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticEntity {
  return !!entity && entity.tags.includes(WINDOW_TRIM);
}

export function isExteriorOpeningAnalyticFace(entity: BuildingAnalyticEntity): entity is BuildingAnalyticFace {
  return (
    isBuildingAnalyticFace(entity) &&
    hasAnalyticCondition(entity, "EXTERIOR") &&
    (hasAnalyticCondition(entity, "WINDOW") || hasAnalyticCondition(entity, "DOOR")) &&
    hasGeomPosition(entity, "BACK")
  );
}

export function isExteriorOpeningUnitAnalyticFace(entity: BuildingAnalyticEntity): entity is BuildingAnalyticFace {
  return isExteriorOpeningAnalyticFace(entity) && !hasAnalyticCondition(entity, "OPEN");
}

export function isExteriorOpeningCasingClipsFinishAnalyticFace(
  entity: BuildingAnalyticEntity,
): entity is BuildingAnalyticFace {
  return (
    isBuildingAnalyticFace(entity) &&
    hasGeomPosition(entity, "BACK") &&
    (hasAnalyticCondition(entity, "WINDOW_TRIM") || hasAnalyticCondition(entity, "DOOR_TRIM")) &&
    hasParentTag(entity, "EXTERIOR_OPENING_TRIM") &&
    hasOperationTag(entity, "CLIP_EXTERIOR_FINISH")
  );
}

export function isWallFinish(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticEntity {
  return !!entity && (isWallExteriorFinish(entity) || isWallInteriorFinish(entity));
}

export function isWallExteriorFinish(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticEntity {
  return !!entity && entity.tags.includes(EXTERIOR_FINISH_TAG);
}

export function isWallExteriorFinishBRep(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticBRep {
  return isBuildingAnalyticBRep(entity) && isWallExteriorFinish(entity);
}

export function isWallInteriorFinish(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticEntity {
  return !!entity && entity.tags.includes(INTERIOR_FINISH_TAG);
}

export function isInteriorTrim(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticEntity {
  return !!entity && parentBuildingHandles(entity).some((p) => p.entityType === "INTERIOR_TRIM");
}

export function isFloorStructure(entity?: BuildingAnalyticEntity): entity is BuildingAnalyticEntity {
  return !!entity && entity.tags.includes(FLOOR_STRUCTURE);
}

export function isWallDomainOpeningIntersection(entity: BuildingAnalyticEntity): entity is BuildingAnalyticFace {
  return isBuildingAnalyticFace(entity) && entity.tags.includes(WALL_DOMAIN_OPENING_INTERSECTION);
}

export function isExteriorFinishEdge(entity: BuildingAnalyticEntity): entity is BuildingAnalyticEntity {
  return includesTags(entity, [EXTERIOR_FINISH_TAG, EDGE]);
}

function hasGeomPosition(entity: BuildingAnalyticEntity, position: FacePosition): boolean {
  return geomPositionTags(entity).some((tag) => tag.position === position);
}

function hasAnalyticCondition(entity: BuildingAnalyticEntity, condition: string): boolean {
  return geomAnalyticTags(entity).some((tag) => tag.condition === condition);
}

function hasParentTag(entity: BuildingAnalyticEntity, parentType: BuildingGeomParentType): boolean {
  return geomParentTags(entity).some((tag) => tag.parentType === parentType);
}

function hasOperationTag(entity: BuildingAnalyticEntity, operation: OperationType): boolean {
  return geomOperationTags(entity).some((tag) => tag.operation === operation);
}

function includesTags(entity: BuildingAnalyticEntity, tags: BuildingGeomTag[]): boolean {
  return tags.every((tag) => entity.tags.includes(tag));
}
